import { ref } from "vue";
import { defineStore } from "pinia";
import { useToasterStore } from "@comp-lib/stores";
import { isDns } from "@core/validation";
import type { ImageData } from "@/components/ImageUploader.vue";
import { HttpError } from "@core/http";
import { getRootSettings, createRootSettings, updateRootSettings, getSmtpSetting, createSmtpSetting, updateSmtpSetting, listDomains, addDomain, verifyDomain, deleteDomain, testEmailSettings } from "@/services/tenantBrandingService";
import { useCurrentTenant } from "@/composables/useCurrentTenant";
import type { TenantBrandingPreferences } from "@/types";

export const useTenantBrandingStore = defineStore("tenantBranding", () => {
  const currentTenant = useCurrentTenant();
  
  const toasterStore = useToasterStore();
  const { triggerNotif } = toasterStore;

  const companyLogoImage = ref<ImageData | null>(null);
  const previewLogo = ref("");
  const loading = ref(false);

  const loadingStates = ref({
    deletingDomains: {} as Record<string, boolean>,
    verifyingDomains: {} as Record<string, boolean>,
    addingDomain: false,
    fetchingSettings: false,
    updatingBranding: false,
    updatingEmail: false,
    testingEmail: false
  });

  const initialPreferences = ref<TenantBrandingPreferences>({
    name: "",
    website: "",
    phone: "",
    mailingAddress: "",
    mailServerType: "direct",
    fromName: "",
    fromEmail: "",
    companyLogo: null,
    smtpSettings: {
      smtpServer: "",
      username: "",
      password: "",
      encryptionType: "SSL",
      portNumber: 587
    },
    domains: [],
    isConfigured: false
  });

  const preferences = ref<TenantBrandingPreferences>(JSON.parse(JSON.stringify(initialPreferences.value)));

  async function getTenantBrandingSettings(): Promise<void> {
    loading.value = true;
    loadingStates.value.fetchingSettings = true;
    
    try {
      // Get root settings
      const rootSettings = await getRootSettings(currentTenant.value.id);
      
      // Map root settings to preferences
      preferences.value.name = rootSettings.name || currentTenant.value.name || "";
      preferences.value.website = rootSettings.website || "";
      preferences.value.phone = rootSettings.phone || "";
      preferences.value.mailingAddress = rootSettings.mailing_address || "";
      preferences.value.mailServerType = rootSettings.mail_server_type || "direct";
      preferences.value.fromName = rootSettings.from_name || "";
      preferences.value.fromEmail = rootSettings.from_email || "";
      preferences.value.isConfigured = true;
      
      if (preferences.value.mailServerType === "smtp") {
        try {
          const smtpSettings = await getSmtpSetting(currentTenant.value.id);
          preferences.value.smtpSettings = {
            smtpServer: smtpSettings.smtp_server || "",
            username: smtpSettings.username || "",
            password: smtpSettings.password || "",
            encryptionType: smtpSettings.encryption_type || "SSL",
            portNumber: smtpSettings.port_number || 587
          };
        } catch {
          console.log("SMTP settings not found, using defaults");
        }
      }
      
      // Get domains list
      try {
        const domains = await listDomains(currentTenant.value.id);
        preferences.value.domains = domains;
      } catch {
        console.log("Domains not found, using empty list");
        preferences.value.domains = [];
      }
      
      initialPreferences.value = JSON.parse(JSON.stringify(preferences.value));
      
    } catch (err) {
      // If root settings don't exist (404), we'll create them on first save
      if (!HttpError.isNotFound(err)) {
        triggerNotif({
          type: "error",
          title: "Error fetching tenant branding settings",
          bodyText: "An error occurred while fetching your settings. Please try again, or contact us for support."
        });
      }
    } finally {
      loading.value = false;
      loadingStates.value.fetchingSettings = false;
    }
  }

  function uploadImage(imageData: ImageData | null) {
    companyLogoImage.value = imageData;
    previewLogo.value = imageData ? imageData.dataUrl : "";
  }
  
  function addHttpsToUrl(value: string) {
    let finalUrl: string | null = null;

    if (isDns(value)) finalUrl = `https://${value}`;
    
    try {
      const url = new URL(value);
      // If the protocol is not http: or https:, force it to https:
      if (!/^https?:$/.test(url.protocol)) url.protocol = "https:";
      finalUrl = url.toString();
    } catch { finalUrl = null }
    
    if (finalUrl) preferences.value.website = finalUrl;
  }

  async function saveRootSettings(section: "branding" | "email"): Promise<void> {
    const current = preferences.value;
    const initial = initialPreferences.value;

    const rootSettingsPayload = {
      name: current.name,
      website: section === "branding" ? current.website : initial.website,
      phone: section === "branding" ? current.phone : initial.phone,
      mailing_address: section === "branding" ? current.mailingAddress : initial.mailingAddress,
      mail_server_type: section === "email" ? current.mailServerType : initial.mailServerType,
      from_name: section === "email" ? current.fromName : initial.fromName,
      from_email: section === "email" ? current.fromEmail : initial.fromEmail
    };

    try {
      await updateRootSettings(currentTenant.value.id, rootSettingsPayload);
    } catch (updateError) {
      if (HttpError.isNotFound(updateError)) {
        await createRootSettings(currentTenant.value.id, rootSettingsPayload);
      } else {
        throw updateError;
      }
    }
  }

  async function updateCompanyBranding(): Promise<void> {
    loadingStates.value.updatingBranding = true;

    try {
      await saveRootSettings("branding");

      triggerNotif({
        type: "success",
        title: "Company branding updated successfully"
      });

      companyLogoImage.value = null;
      initialPreferences.value.website = preferences.value.website;
      initialPreferences.value.phone = preferences.value.phone;
      initialPreferences.value.mailingAddress = preferences.value.mailingAddress;

    } catch {
      triggerNotif({
        type: "error",
        title: "Error updating company branding",
        bodyText: "An error occurred while updating your settings. Please try again, or contact us for support."
      });
    } finally {
      loadingStates.value.updatingBranding = false;
    }
  }

  async function updateEmailSettings(): Promise<void> {
    loadingStates.value.updatingEmail = true;

    try {
      await saveRootSettings("email");

      if (preferences.value.mailServerType === "smtp") {
        const smtpPayload = {
          smtp_server: preferences.value.smtpSettings.smtpServer,
          username: preferences.value.smtpSettings.username,
          password: preferences.value.smtpSettings.password,
          encryption_type: preferences.value.smtpSettings.encryptionType,
          port_number: preferences.value.smtpSettings.portNumber
        };

        try {
          await updateSmtpSetting(currentTenant.value.id, smtpPayload);
        } catch (smtpUpdateError) {
          if (HttpError.isNotFound(smtpUpdateError)) {
            await createSmtpSetting(currentTenant.value.id, smtpPayload);
          } else {
            throw smtpUpdateError;
          }
        }
      }

      triggerNotif({
        type: "success",
        title: "Email preferences updated successfully"
      });

      initialPreferences.value.mailServerType = preferences.value.mailServerType;
      initialPreferences.value.fromName = preferences.value.fromName;
      initialPreferences.value.fromEmail = preferences.value.fromEmail;
      initialPreferences.value.smtpSettings = JSON.parse(JSON.stringify(preferences.value.smtpSettings));

    } catch {
      triggerNotif({
        type: "error",
        title: "Error updating email preferences",
        bodyText: "An error occurred while updating your settings. Please try again, or contact us for support."
      });
    } finally {
      loadingStates.value.updatingEmail = false;
    }
  }

  async function addEmailDomain(domain: string): Promise<void> {
    loadingStates.value.addingDomain = true;
    
    try {
      const result = await addDomain(currentTenant.value.id, { domain });
      preferences.value.domains.push(result);
      
      triggerNotif({
        type: "success",
        title: "Domain added successfully"
      });
      
    } catch (error) {
      triggerNotif({
        type: "error",
        title: "Error adding domain",
        bodyText: "An error occurred while adding the domain. Please try again, or contact us for support."
      });
      throw error;
    } finally {
      loadingStates.value.addingDomain = false;
    }
  }

  async function verifyEmailDomain(domainId: string): Promise<void> {
    loadingStates.value.verifyingDomains[domainId] = true;
    
    try {
      const result = await verifyDomain(currentTenant.value.id, domainId);
      
      const index = preferences.value.domains.findIndex(domain => domain.id === domainId);
      if (index !== -1) {
        preferences.value.domains[index] = result;
      }
      
      const isVerified = result.cname_verified === "success" && 
                        result.dkim1_verified === "success" && 
                        result.dkim2_verified === "success";
      
      triggerNotif({
        type: isVerified ? "success" : "error",
        title: isVerified ? "Domain verification completed" : "Domain verification unsuccessful",
        bodyText: isVerified ? "Domain verified successfully." : "Please check your DNS settings."
      });
      
    } catch (error) {
      triggerNotif({
        type: "error",
        title: "Error verifying domain",
        bodyText: "An error occurred while verifying the domain. Please try again, or contact us for support."
      });
      throw error;
    } finally {
      loadingStates.value.verifyingDomains[domainId] = false;
    }
  }

  async function testEmailPreferences(): Promise<void> {
    loadingStates.value.testingEmail = true;

    try {
      await testEmailSettings(currentTenant.value.id);
      triggerNotif({
        type: "success",
        title: "Test email sent",
        bodyText: "A test email has been sent to your email address."
      });
    } catch {
      triggerNotif({
        type: "error",
        title: "Error testing email settings",
        bodyText: "An error occurred while testing your email settings. Please try again, or contact us for support."
      });
    } finally {
      loadingStates.value.testingEmail = false;
    }
  }

  async function removeEmailDomain(domainId: string): Promise<void> {
    loadingStates.value.deletingDomains[domainId] = true;
    
    try {
      await deleteDomain(currentTenant.value.id, domainId);
      
      const index = preferences.value.domains.findIndex(domain => domain.id === domainId);
      if (index !== -1) {
        preferences.value.domains.splice(index, 1);
      }
      
      triggerNotif({
        type: "success",
        title: "Domain removed successfully"
      });
      
    } catch (error) {
      triggerNotif({
        type: "error",
        title: "Error removing domain",
        bodyText: "An error occurred while removing the domain. Please try again, or contact us for support."
      });
      throw error;
    } finally {
      loadingStates.value.deletingDomains[domainId] = false;
    }
  }

  return { companyLogoImage, previewLogo, loading, loadingStates, initialPreferences, preferences, getTenantBrandingSettings, uploadImage, addHttpsToUrl, updateCompanyBranding, updateEmailSettings, testEmailPreferences, addEmailDomain, verifyEmailDomain, removeEmailDomain };
});
